/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-loss-of-precision */
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subject, debounceTime, takeUntil } from "rxjs";
import { MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { MenuModule } from "primeng/menu";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
import { TableModule } from "primeng/table";
import {
  OrdersRevenueDailyAggregatedParams,
  OrdersRevenueDailyAggregatedResponse,
} from "../../interfaces/revenue-analytics.interface";
import { RevenueFiltersForm } from "../../interfaces/revenue-filters-form.interface";
import { LastIngestedOrder } from "../../interfaces/analytics-status.interface";
import { DateFilterComponent } from "../date-filter/date-filter.component";
import { RevenueCardSkeletonComponent } from "../revenue-card-skeleton/revenue-card-skeleton.component";
import { RevenueComparisonTableSkeletonComponent } from "../revenue-comparison-table-skeleton/revenue-comparison-table-skeleton.component";
import {
  RevenueCardComponent,
  RevenueCardData,
} from "../revenue-card/revenue-card.component";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

@Component({
  selector: "app-revenue-dashboard",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    ToastModule,
    MenuModule,
    TableModule,
    DateFilterComponent,
    RevenueCardComponent,
    RevenueCardSkeletonComponent,
    RevenueComparisonTableSkeletonComponent,
  ],
  templateUrl: "./revenue-dashboard.component.html",
  styleUrl: "./revenue-dashboard.component.scss",
})
export class RevenueDashboardComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode
  @Input() inputSiteId?: string | null;
  @Input() inputToken?: QPilotAccessToken | null;
  @Input() embeddedMode: boolean = false;

  // Input to track if this tab is active (for lazy loading)
  private _active = false;
  private hasLoadedData = false;

  @Input() set active(value: boolean) {
    this._active = value;
    // Load data only once when tab becomes active for the first time
    // Only load if we have both siteId and accessToken
    if (value && !this.hasLoadedData && this.siteId && this.getAccessToken()) {
      this.hasLoadedData = true;
      this.loadData();
    }
  }

  get active(): boolean {
    return this._active;
  }

  @Input() lastIngestedOrder: LastIngestedOrder | null = null; // Last ingested order from parent

  isLoading = true;
  revenueData: RevenueCardData[] = [];
  skeletonArray = [1, 2, 3]; // For rendering 3 skeleton cards
  downloadMenuItems: MenuItem[] = [];
  lastUpdated: string | null = null; // For displaying ETL updated timestamp

  // Sidebar collapse state
  sidebarCollapsed = signal<boolean>(false);
  private userManuallyCollapsed = false; // Track if user manually toggled sidebar
  private readonly destroy$ = new Subject<void>();

  // View mode state: 'cards' or 'table'
  viewMode = signal<"cards" | "table">("cards");

  // Animated table values
  animatedTableData: ComparisonTableRow[] = [];
  private isAnimating = false;

  // Strongly-typed reactive form for filters
  filtersFormGroup!: FormGroup<RevenueFiltersForm>;

  // Default currency from site settings
  defaultCurrency = signal<string>("USD");

  // Site timezone offset (e.g., -5 for EST, +1 for CET)
  siteUtcOffset = signal<number | null>(null);

  // Site ID from route
  private siteId: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    // CRITICAL: Initialize form FIRST before any subscriptions that might call loadData()
    this.initializeFiltersForm();
    this.initializeDownloadMenu();

    // Check if in embedded mode with inputs provided
    if (this.embeddedMode && this.inputSiteId) {
      this.siteId = parseInt(this.inputSiteId, 10);

      // Fetch site details to get default currency
      if (this.getAccessToken()) {
        this.loadSiteCurrency();
      }

      // Load data immediately when siteId AND accessToken are available
      if (!this.hasLoadedData && this.getAccessToken()) {
        this.hasLoadedData = true;
        this.loadData();
      }
    } else {
      // Standard mode: Get siteId from route params
      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const siteIdParam = params.get("siteId");
        if (siteIdParam) {
          this.siteId = parseInt(siteIdParam, 10);

          // Fetch site details to get default currency
          if (this.getAccessToken()) {
            this.loadSiteCurrency();
          }

          // Load data immediately when siteId AND accessToken are available
          if (!this.hasLoadedData && this.getAccessToken()) {
            this.hasLoadedData = true;
            this.loadData();
          }
        }
      });
    }

    this.subscribeToFilterChanges();
    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
  }

  /**
   * Get access token based on current mode
   * In embedded mode, use inputToken; in standard mode, use sessionService
   */
  private getAccessToken(): QPilotAccessToken | null {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the filters form with default values
   * Currency will be loaded from site settings (defaults to USD)
   * Date range starts empty - API returns last 30 days by default
   */
  private initializeFiltersForm(): void {
    // Create form with empty date range (API defaults to last 30 days)
    // Currency starts as USD and will be updated when site data loads
    this.filtersFormGroup = this.formBuilder.group<RevenueFiltersForm>({
      dateRange: this.formBuilder.nonNullable.control<Date[]>([]),
      compareTo: this.formBuilder.control<Date[] | null>(null),
      currency: this.formBuilder.nonNullable.control<string>(
        this.defaultCurrency(),
      ),
    });
  }

  /**
   * Subscribe to form value changes and reload data
   * Uses debounce to avoid excessive API calls
   */
  private subscribeToFilterChanges(): void {
    this.filtersFormGroup.valueChanges
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing/changing
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => {
        // Load data - API supports no dates (will use defaults)
        // Load if start date is selected (end date is optional) OR if date range is empty/cleared
        const hasStartDate =
          filters.dateRange &&
          filters.dateRange.length > 0 &&
          filters.dateRange[0];

        const isDateRangeCleared =
          !filters.dateRange || filters.dateRange.length === 0;

        if (hasStartDate || isDateRangeCleared) {
          this.loadData();
        }
      });
  }

  private initializeDownloadMenu() {
    this.downloadMenuItems = [
      {
        label: "Export Results - Spreadsheet (.CSV)",
        icon: "pi pi-file",
        command: () => this.downloadCSV(),
      },
      {
        label: "Export Results - Markdown (.MD)",
        icon: "pi pi-file-edit",
        command: () => this.downloadMarkdown(),
      },
    ];
  }

  /**
   * Load site currency and timezone from sitefull API
   * Extracts CurrencyIso and UtcOffset from site data
   */
  private loadSiteCurrency(): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) {
      return;
    }

    this.qpilotService
      .getSiteFull(this.siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site) => {
          // Extract CurrencyIso from site, default to USD if not available
          const currency = site.CurrencyIso || "USD";
          this.defaultCurrency.set(currency);

          // Extract UtcOffset from site (e.g., -5 for EST, +1 for CET)
          if (site.UtcOffset !== undefined && site.UtcOffset !== null) {
            this.siteUtcOffset.set(site.UtcOffset);
          }

          // Update the form control with the site's default currency
          this.filtersFormGroup.controls.currency.setValue(currency, {
            emitEvent: false, // Don't trigger loadData() on initial currency set
          });
        },
        error: (error) => {
          console.error("Error loading site currency:", error);
          // Keep default USD currency on error
        },
      });
  }

  /**
   * Load revenue data from the API
   * Calls the daily aggregated endpoint which returns current/previous period comparison
   */
  private loadData(): void {
    this.isLoading = true;

    const accessToken = this.getAccessToken();

    // Check if we have siteId and access token
    if (!this.siteId || !accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Session information not available. Please log in again.",
      });
      this.isLoading = false;
      return;
    }

    // Build API params from form values
    const params = this.buildApiParams();

    // Call new daily aggregated API endpoint
    this.qpilotService
      .getOrdersRevenueDailyAggregated(this.siteId, accessToken, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.processApiResponse(response);
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading revenue data:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to load revenue data. Please try again.",
          });
          this.isLoading = false;
        },
      });
  }

  /**
   * Transform API response into RevenueCardData format
   * Uses currentPeriod data for values and comparison for percentage changes
   */
  private processApiResponse(
    response: OrdersRevenueDailyAggregatedResponse,
  ): void {
    if (!response || !response.currentPeriod || !response.comparison) {
      this.revenueData = [];
      this.lastUpdated = null;
      this.animatedTableData = [];
      return;
    }

    const { currentPeriod, previousPeriod, comparison } = response;

    // Set the compareTo date range from previousPeriod
    if (previousPeriod && previousPeriod.dateFrom && previousPeriod.dateTo) {
      // Parse dates as local dates to avoid timezone offset issues
      // API returns ISO strings like "2025-10-06T00:00:00Z"
      // We want to display the date part only (Oct 6), not shifted by timezone
      const compareToRange = [
        this.parseApiDateAsLocal(previousPeriod.dateFrom),
        this.parseApiDateAsLocal(previousPeriod.dateTo),
      ];
      this.filtersFormGroup.controls.compareTo.setValue(compareToRange, {
        emitEvent: false,
      });
    }

    this.revenueData = [
      {
        title: "ALL REVENUE",
        amount: currentPeriod.allRevenue,
        percentageChange: comparison.allRevenuePctChange,
        orders: currentPeriod.allOrders,
        ordersChange: comparison.allOrdersPctChange,
        aov: currentPeriod.allAov,
        aovChange: comparison.allAovPctChange,
        colorClass: "all-revenue",
      },
      {
        title: "SUBSCRIPTION",
        amount: currentPeriod.subscriptionRevenue,
        percentageChange: comparison.subscriptionRevenuePctChange,
        orders: currentPeriod.subscriptionOrders,
        ordersChange: comparison.subscriptionOrdersPctChange,
        aov: currentPeriod.subscriptionAov,
        aovChange: comparison.subscriptionAovPctChange,
        colorClass: "subscription",
      },
      {
        title: "NON-SUBSCRIPTION",
        amount: currentPeriod.nonSubscriptionRevenue,
        percentageChange: comparison.nonSubscriptionRevenuePctChange,
        orders: currentPeriod.nonSubscriptionOrders,
        ordersChange: comparison.nonSubscriptionOrdersPctChange,
        aov: currentPeriod.nonSubscriptionAov,
        aovChange: comparison.nonSubscriptionAovPctChange,
        colorClass: "non-subscription",
      },
    ];

    // Set lastUpdated to null since the new API doesn't provide it
    // Could potentially use currentPeriod.dateTo if needed
    this.lastUpdated = null;

    // Update table data when in table view mode
    if (this.viewMode() === "table") {
      this.animateTableNumbers();
    }
  }

  /**
   * Build query parameters for API call
   * All parameters are optional - API defaults to last 30 days
   * End date is optional - only start date is required if providing dates
   */
  private buildApiParams(): OrdersRevenueDailyAggregatedParams | undefined {
    const filters = this.filtersFormGroup.value;

    // Check if we have a start date
    const hasStartDate =
      filters.dateRange && filters.dateRange.length > 0 && filters.dateRange[0];

    // If no start date, return params with just currency
    if (!hasStartDate || !filters.dateRange) {
      return {
        currency: filters.currency || undefined,
      };
    }

    const startDate = filters.dateRange[0];
    const endDate =
      filters.dateRange.length === 2 ? filters.dateRange[1] : null;

    return {
      dateCreatedFrom: this.formatDateForApi(startDate),
      dateCreatedTo: endDate ? this.formatDateForApi(endDate) : undefined,
      currency: filters.currency || undefined,
    };
  }

  /**
   * Format date to YYYY-MM-DD for API
   */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Parse API date string as local date to avoid timezone offset issues
   * API returns ISO strings like "2025-10-06T00:00:00Z"
   * We want to display "Oct 6" not "Oct 5" (which would happen with timezone offset)
   */
  private parseApiDateAsLocal(isoString: string): Date {
    // Extract just the date part (YYYY-MM-DD) and create local date
    const datePart = isoString.split("T")[0]; // "2025-10-06"
    const [year, month, day] = datePart.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  downloadCSV() {
    // Convert revenue data to CSV format
    const headers = [
      "Category",
      "Amount",
      "Amount Change %",
      "Orders",
      "Orders Change %",
      "AOV",
      "AOV Change %",
    ];

    const rows = this.revenueData.map((card) => [
      card.title,
      this.truncateToDecimals(card.amount, 2).toFixed(2),
      this.truncateToDecimals(card.percentageChange, 2).toFixed(2) + "%",
      card.orders.toString(),
      this.truncateToDecimals(card.ordersChange, 2).toFixed(2) + "%",
      card.aov !== null
        ? this.truncateToDecimals(card.aov, 2).toFixed(2)
        : "N/A",
      card.aovChange !== null
        ? this.truncateToDecimals(card.aovChange, 2).toFixed(2) + "%"
        : "N/A",
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    // Generate filename with current date
    const date = new Date().toISOString().split("T")[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `revenue-report-${date}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadMarkdown() {
    // Build markdown content
    let markdown = "# Subscription vs. Non-Subscription Revenue\n\n";
    markdown +=
      "_See how your QPilot subscription orders contribute to your overall retail sales._\n\n";

    // Create a table for each revenue category
    this.revenueData.forEach((card, index) => {
      if (index > 0) markdown += "\n";

      markdown += `## ${card.title}\n\n`;

      // Main amount with change
      const changeSymbol = card.percentageChange >= 0 ? "+" : "";
      markdown += `**Revenue:** $${this.formatNumber(card.amount)} (${changeSymbol}${this.truncateToDecimals(card.percentageChange, 2).toFixed(2)}%)\n\n`;

      // Metrics table
      markdown += "| Metric | Value | Change |\n";
      markdown += "|--------|-------|--------|\n";

      // Orders row
      const ordersChangeSymbol = card.ordersChange >= 0 ? "+" : "";
      markdown += `| Orders | ${this.formatNumber(card.orders)} | ${ordersChangeSymbol}${this.truncateToDecimals(card.ordersChange, 2).toFixed(2)}% |\n`;

      // AOV row
      const aovValue =
        card.aov !== null
          ? `$${this.truncateToDecimals(card.aov, 2).toFixed(2)}`
          : "N/A";
      const aovChangeSymbol =
        card.aovChange !== null && card.aovChange >= 0 ? "+" : "";
      const aovChangeValue =
        card.aovChange !== null
          ? `${aovChangeSymbol}${this.truncateToDecimals(card.aovChange, 2).toFixed(2)}%`
          : "N/A";
      markdown += `| AOV | ${aovValue} | ${aovChangeValue} |\n`;

      markdown += "\n";
    });

    // Add summary table
    markdown += "## Summary Table\n\n";
    markdown +=
      "| Category | Amount | Change | Orders | Orders Change | AOV | AOV Change |\n";
    markdown +=
      "|----------|--------|--------|--------|---------------|-----|------------|\n";

    this.revenueData.forEach((card) => {
      const amountChangeSymbol = card.percentageChange >= 0 ? "+" : "";
      const ordersChangeSymbol = card.ordersChange >= 0 ? "+" : "";
      const aovChangeSymbol =
        card.aovChange !== null && card.aovChange >= 0 ? "+" : "";

      const aovValue =
        card.aov !== null
          ? `$${this.truncateToDecimals(card.aov, 2).toFixed(2)}`
          : "N/A";
      const aovChangeValue =
        card.aovChange !== null
          ? `${aovChangeSymbol}${this.truncateToDecimals(card.aovChange, 2).toFixed(2)}%`
          : "N/A";

      markdown += `| ${card.title} | $${this.formatNumber(card.amount)} | ${amountChangeSymbol}${this.truncateToDecimals(card.percentageChange, 2).toFixed(2)}% | `;
      markdown += `${this.formatNumber(card.orders)} | ${ordersChangeSymbol}${this.truncateToDecimals(card.ordersChange, 2).toFixed(2)}% | `;
      markdown += `${aovValue} | ${aovChangeValue} |\n`;
    });

    markdown += "\n---\n\n";
    markdown += `_Report generated on ${new Date().toLocaleString()}_\n`;

    // Create blob and download
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    // Generate filename with current date
    const date = new Date().toISOString().split("T")[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `revenue-report-${date}.md`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private formatNumber(num: number): string {
    const truncated = this.truncateToDecimals(num, 2);
    return truncated.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Truncates a number to specified decimal places WITHOUT any rounding.
   * Just cuts off everything after the specified decimals.
   * Example: truncateToDecimals(44.49830645161905, 2) => 44.49 (not 44.50)
   * Example: truncateToDecimals(-72.353954, 2) => -72.35 (not -72.36)
   */
  private truncateToDecimals(value: number, decimals: number = 2): number {
    const multiplier = Math.pow(10, decimals);
    return Math.trunc(value * multiplier) / multiplier;
  }

  /**
   * Convert UtcOffset number to timezone string for Angular date pipe
   * Example: -5 => '-0500', +5.5 => '+0530', 0 => '+0000'
   */
  getSiteTimezone(): string {
    const offset = this.siteUtcOffset();
    if (offset === null || offset === undefined) {
      return ""; // Let Angular use browser's local timezone as fallback
    }

    // Convert offset to hours and minutes
    const hours = Math.floor(Math.abs(offset));
    const minutes = Math.round((Math.abs(offset) - hours) * 60);

    // Format as +HHMM or -HHMM
    const sign = offset >= 0 ? "+" : "-";
    const hoursStr = String(hours).padStart(2, "0");
    const minutesStr = String(minutes).padStart(2, "0");

    return `${sign}${hoursStr}${minutesStr}`;
  }

  /**
   * Toggle sidebar collapse state
   */
  toggleSidebar(): void {
    const newState = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(newState);
    this.userManuallyCollapsed = newState; // Track user preference
  }

  /**
   * Check if any filters are active (non-default values)
   */
  hasActiveFilters(): boolean {
    const formValue = this.filtersFormGroup.value;

    // Check if compareTo has a value (it starts as null, so any value means active)
    const hasCompareTo =
      formValue.compareTo && formValue.compareTo.length === 2;

    // Check if dateRange has a value (starts empty, so any value means active)
    const hasDateRange =
      formValue.dateRange && formValue.dateRange.length === 2;

    // Currency is no longer a user-selectable filter, so we don't check it

    return !!(hasCompareTo || hasDateRange);
  }

  /**
   * Initialize responsive sidebar based on viewport width
   */
  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      // Only auto-collapse on mobile if user hasn't manually set preference
      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        // Only auto-expand on desktop if user hasn't manually collapsed
        this.sidebarCollapsed.set(false);
      }
      // If user manually toggled, respect their preference regardless of screen size

      this.cdr.markForCheck();
    });

    // Observe the window for resize changes
    resizeObserver.observe(document.body);

    // Clean up observer on destroy
    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }

  /**
   * Toggle between cards and table view
   * Triggers animation when switching to table view
   */
  toggleViewMode(): void {
    const newMode = this.viewMode() === "cards" ? "table" : "cards";
    this.viewMode.set(newMode);

    // Trigger animation when switching to table view
    if (newMode === "table") {
      this.animateTableNumbers();
    }
  }

  /**
   * Animate table numbers from 0 to final values
   * Uses requestAnimationFrame with ease-out cubic easing
   * Similar to the animation in revenue-card component
   */
  private animateTableNumbers(): void {
    if (this.isAnimating) return;

    const finalData = this.getComparisonTableData();
    if (finalData.length === 0) return;

    this.isAnimating = true;

    // Initialize animated data with zeros
    this.animatedTableData = finalData.map((row) => ({
      ...row,
      allRevenue: 0,
      allRevenueChange: 0,
      subscription: 0,
      subscriptionChange: 0,
      nonSubscription: 0,
      nonSubscriptionChange: 0,
    }));

    const steps = 60;
    let currentStep = 0;

    const animate = () => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOutProgress = 1 - Math.pow(1 - progress, 3); // Ease-out cubic

      // Animate all values
      this.animatedTableData = finalData.map((row) => ({
        metric: row.metric,
        format: row.format,
        allRevenue: row.allRevenue * easeOutProgress,
        allRevenueChange: row.allRevenueChange * easeOutProgress,
        subscription: row.subscription * easeOutProgress,
        subscriptionChange: row.subscriptionChange * easeOutProgress,
        nonSubscription: row.nonSubscription * easeOutProgress,
        nonSubscriptionChange: row.nonSubscriptionChange * easeOutProgress,
      }));

      this.cdr.markForCheck();

      if (currentStep < steps) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final values are exact
        this.animatedTableData = finalData;
        this.isAnimating = false;
        this.cdr.markForCheck();
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Get comparison table data
   * Transforms revenueData into rows for the comparison table
   */
  getComparisonTableData(): ComparisonTableRow[] {
    if (this.revenueData.length === 0) {
      return [];
    }

    return [
      {
        metric: "Revenue",
        allRevenue: this.revenueData[0]?.amount || 0,
        allRevenueChange: this.revenueData[0]?.percentageChange || 0,
        subscription: this.revenueData[1]?.amount || 0,
        subscriptionChange: this.revenueData[1]?.percentageChange || 0,
        nonSubscription: this.revenueData[2]?.amount || 0,
        nonSubscriptionChange: this.revenueData[2]?.percentageChange || 0,
        format: "currency",
      },
      {
        metric: "Orders",
        allRevenue: this.revenueData[0]?.orders || 0,
        allRevenueChange: this.revenueData[0]?.ordersChange || 0,
        subscription: this.revenueData[1]?.orders || 0,
        subscriptionChange: this.revenueData[1]?.ordersChange || 0,
        nonSubscription: this.revenueData[2]?.orders || 0,
        nonSubscriptionChange: this.revenueData[2]?.ordersChange || 0,
        format: "number",
      },
      {
        metric: "AOV",
        allRevenue: this.revenueData[0]?.aov || 0,
        allRevenueChange: this.revenueData[0]?.aovChange || 0,
        subscription: this.revenueData[1]?.aov || 0,
        subscriptionChange: this.revenueData[1]?.aovChange || 0,
        nonSubscription: this.revenueData[2]?.aov || 0,
        nonSubscriptionChange: this.revenueData[2]?.aovChange || 0,
        format: "currency",
      },
    ];
  }
}

// Interface for comparison table rows
interface ComparisonTableRow {
  metric: string;
  allRevenue: number;
  allRevenueChange: number;
  subscription: number;
  subscriptionChange: number;
  nonSubscription: number;
  nonSubscriptionChange: number;
  format: "currency" | "number";
}
